import { invoke } from '@tauri-apps/api/core';

export interface SessionsObj {
    all: string[];
    arp: string[];
    headless: { id: string; instance: string }[];
}

interface RtcComponentOptions {
    onSessions?: (obj: SessionsObj) => void;
    onUnauthorized: () => Promise<string>;
    publicDiscovery?: boolean;
    refreshToken: () => Promise<string>;
    signal: AbortSignal;
    token: string;
}

interface NewIceCandidate {
    candidate: RTCIceCandidateInit;
    type: 'newIceCandidate';
}

interface Sessions {
    all: string[];
    arp: string[];
    headless: { id: string; instance: string }[];
    type: 'sessions';
}

interface Offer {
    from: string;
    sdp: RTCSessionDescriptionInit;
    to: string;
    type: 'offer';
}

interface Answer {
    sdp: RTCSessionDescriptionInit;
    type: 'answer';
}

interface Join {
    identity: string;
    type: 'join';
}

const errorTypes = {
    CANCEL: {
        code: 4,
        message: 'Discovery was cancelled',
    },
    ERR_RESPONSE: {
        code: 2,
        message: 'Error response from server',
    },
    PARSE: {
        code: 3,
        message: 'Error parsing response',
    },
    TIMEOUT: {
        code: 1,
        message: 'Timeout while waiting for response',
    },
};

export class RtcComponentError extends Error {
    code: number;
    detail?: string;
    constructor(errorType: keyof typeof errorTypes, detail?: string) {
        super(errorTypes[errorType].message);
        this.name = 'RtcComponentError';
        this.code = errorTypes[errorType].code;
        this.detail = detail;
    }
}

export type WsIceMessage = NewIceCandidate | Sessions | Offer | Answer | Join;

export class RtcComponent {
    rtc?: RTCPeerConnection;
    ws?: WebSocket;
    onSessions: RtcComponentOptions['onSessions'];
    onUnauthorized: RtcComponentOptions['onUnauthorized'];
    refreshToken: RtcComponentOptions['refreshToken'];
    target?: string;
    identity?: string;
    dataChannel?: RTCDataChannel;
    mode: 'browser' | 'headless' = 'browser';
    listening = false;
    timeout?: NodeJS.Timeout;
    promiseObj?: { rej: (err: unknown) => void; res: (val: string) => void };
    #publicDiscovery: boolean;
    /** Wait for remote description to be set before adding candidates to connection */
    queuedCandidates: RTCIceCandidate[] = [];
    addedCandidates: RTCIceCandidate[] = [];
    /** Wait for answer before sending candidates */
    candidatesToSend: RTCIceCandidate[] = [];
    signal: AbortSignal;
    token: string;

    constructor({ onSessions, onUnauthorized, publicDiscovery, refreshToken, signal, token }: RtcComponentOptions) {
        this.#publicDiscovery = publicDiscovery ?? false;
        this.signal = signal;
        this.onSessions = onSessions;
        this.onUnauthorized = onUnauthorized;
        this.refreshToken = refreshToken;
        this.token = token;
        this.connect();
    }

    /** Connect to websocket and handle WebRTC signaling */
    connect() {
        this.refreshToken()
            .then((token) => {
                this.token = token;
                this.signal.throwIfAborted();
                const queryParams = new URLSearchParams({
                    arp: '__TAURI_INTERNALS__' in window ? '1' : '0',
                    public: this.#publicDiscovery ? '1' : '0',
                    token: this.token,
                });
                this.ws = new WebSocket(
                    process.env.EXPO_PUBLIC_RENKI_API_HOST
                        ? `${process.env.EXPO_PUBLIC_RENKI_API_HOST}/v1/rtc/ws/?${queryParams.toString()}`
                        : `wss://${process.env.EXPO_PUBLIC_RENKI_API_HOST!}/v1/rtc/ws/?${queryParams.toString()}`,
                );
                let shouldRetry = true;
                this.ws.onopen = () => {
                    console.log('WS Connected');
                };
                this.ws.onmessage = async (msg) => {
                    const json = JSON.parse(msg.data as string) as WsIceMessage;
                    console.log('GOT WS MSG', json);
                    switch (json.type) {
                        case 'newIceCandidate': {
                            if (!this.listening) {
                                console.log('Got ice candidate but dont care');
                                return;
                            }
                            if (!this.rtc) {
                                console.log('NO RTC');
                                return;
                            }
                            const candidate = new RTCIceCandidate(json.candidate);

                            if (this.rtc.remoteDescription) {
                                this.rtc
                                    .addIceCandidate(candidate)
                                    .then(() => {
                                        this.addedCandidates.push(candidate);
                                        console.log('CANDIDATE ADDED');
                                    })
                                    .catch(console.error);
                            } else {
                                console.log('ADDING CANDIDATE TO QUEUE');
                                this.queuedCandidates.push(candidate);
                            }

                            break;
                        }
                        case 'sessions': {
                            if (this.onSessions) {
                                this.onSessions({ all: json.all, arp: json.arp, headless: json.headless });
                            }
                            break;
                        }
                        case 'offer': {
                            if (!this.listening) {
                                console.log('Got offer but dont care');
                                return;
                            }
                            await this.answerBack(json);
                            break;
                        }
                        case 'answer': {
                            if (!this.listening) {
                                console.log('Got answer but dont care');
                                return;
                            }
                            await this.handleAnswer(json);
                            break;
                        }
                        case 'join': {
                            this.identity = json.identity;
                            break;
                        }
                        default: {
                            break;
                        }
                    }
                };
                this.ws.onclose = (e) => {
                    if (e.code === 4000) {
                        console.log('Unauthorized, refreshing token');
                        this.onUnauthorized()
                            .then((res) => {
                                this.token = res;
                                this.connect();
                            })
                            .catch(console.error);
                    } else {
                        console.log(
                            'Socket is closed. Reconnect will be attempted in 5 seconds.',
                            'code',
                            e.code,
                            'reason',
                            e.reason,
                        );
                        this.ws?.close();
                        setTimeout(() => {
                            if (shouldRetry) {
                                shouldRetry = false;
                                this.connect();
                            }
                        }, 5000);
                    }
                };
                this.ws.onerror = (err) => {
                    console.error('Socket encountered error: ', err, 'Closing socket');

                    this.ws?.close();
                    this.ws = undefined;
                    setTimeout(() => {
                        if (shouldRetry) {
                            shouldRetry = false;
                            this.connect();
                        }
                    }, 10_000);
                };
                setTimeout(() => {
                    if (!this.ws || this.ws.readyState === 0) {
                        console.log('WS stuck');
                        this.ws?.close();
                        if (shouldRetry) {
                            shouldRetry = false;
                            this.connect();
                        }
                    }
                }, 15_000);
            })
            .catch((error) => {
                if (error instanceof Error && error.name === 'AbortError') {
                    console.log('WS Aborted');
                    this.ws?.close();
                } else {
                    console.error(error);
                }
            });
    }
    /** Set remote description based on the answer and start sending candidates */
    async handleAnswer(json: Answer) {
        if (this.rtc?.signalingState === 'stable') {
            console.log('### Invalid state');
            return;
        }
        const desc = new RTCSessionDescription(json.sdp);
        await this.rtc!.setRemoteDescription(desc).catch(console.error);
        console.log('SET REMOTE DESCRIPTION');
        for (const candidate of this.candidatesToSend) {
            this.ws?.send(
                JSON.stringify({
                    candidate: candidate,
                    from: this.identity,
                    mode: this.mode,
                    to: this.target,
                    type: 'newIceCandidate',
                }),
            );
        }

        if (this.queuedCandidates.length) {
            for (const candidate of this.queuedCandidates) {
                try {
                    await this.rtc?.addIceCandidate(candidate);
                    this.addedCandidates.push(candidate);
                    console.log('ADDED QUEUED CANDIDATE', candidate);
                } catch (error) {
                    console.log('Failed to add candidate', error);
                }
            }
        } else {
            console.log('NO QUEUED CANDIDATES');
        }
    }

    /** Create an answer to an offer (implicitly through setLocalDescription) */
    async answerBack(json: Offer) {
        if (json.to !== this.identity) {
            console.log('Not meant for me');
            return;
        }
        this.target = json.from;
        if (!this.rtc) {
            this.createRtc(this.mode).catch(console.error);
        }
        const desc = new RTCSessionDescription(json.sdp);
        await this.rtc!.setRemoteDescription(desc);
        await this.rtc!.setLocalDescription();
        if (this.queuedCandidates.length) {
            for (const candidate of this.queuedCandidates) {
                console.log('ADDING QUEUED CANDIDATE', candidate);
                try {
                    await this.rtc?.addIceCandidate(candidate);
                    this.addedCandidates.push(candidate);
                } catch (error) {
                    console.log('Failed to add candidate', error);
                }
            }
        }
        this.ws?.send(
            JSON.stringify({
                from: this.identity,
                mode: this.mode,
                sdp: this.rtc!.localDescription,
                to: this.target,
                type: 'answer',
            }),
        );
    }

    /** Send candidates through websocket once remote description has been set or queue them if not */
    onIceCandidate(e: RTCPeerConnectionIceEvent) {
        if (e.candidate?.candidate) {
            if (!this.rtc?.remoteDescription) {
                this.candidatesToSend.push(e.candidate);
            } else {
                this.ws?.send(
                    JSON.stringify({
                        candidate: e.candidate,
                        from: this.identity,
                        mode: this.mode,
                        to: this.target,
                        type: 'newIceCandidate',
                    }),
                );
            }
        }
    }

    /** If tauri is in scope, run arp on candidate addresses */
    async onDataChannelOpen() {
        console.log('Data channel opened');
        if ('__TAURI_INTERNALS__' in window) {
            let msg = '{"error":"No mac found"}';
            for (const candidate of this.addedCandidates) {
                console.log('Trying candidate', candidate.address);
                try {
                    const res = await invoke('mac_from_ip', { forcePing: false, ip: candidate.address });
                    msg = JSON.stringify({ mac: res });
                    break;
                } catch (error) {
                    console.error(error);
                }
            }
            this.dataChannel?.send(msg);
            if (this.timeout) {
                clearTimeout(this.timeout);
                this.timeout = undefined;
            }
            this.closeRtc();
        } else {
            console.log('No tauri');
        }
    }

    /** Expect a message containing a valid mac for one of the candidates sent */
    onDataChannelMessage(event: MessageEvent) {
        let msg = '';
        switch (typeof event.data) {
            case 'string': {
                msg = event.data;
                break;
            }
            case 'object': {
                // @ts-expect-error Get json string from arraybuffer
                msg = String.fromCharCode.apply(null, new Uint8Array(event.data as ArrayBuffer));
                break;
            }
            default: {
                console.log('Received something, type', typeof event.data);
                return;
            }
        }
        console.log('Received', msg);
        try {
            const json = JSON.parse(msg) as { mac: string } | { error: string };
            if (this.timeout) {
                clearTimeout(this.timeout);
            }
            if ('error' in json) {
                this.promiseObj?.rej(new RtcComponentError('ERR_RESPONSE', json.error));
            } else {
                this.promiseObj?.res(json.mac);
            }
        } catch (error) {
            console.error(error);
            this.promiseObj?.rej(new RtcComponentError('PARSE', `Failed to parse message: ${msg}`));
        } finally {
            this.promiseObj = undefined;
            this.closeRtc();
        }
    }

    /** Create a new peerConnection and an offer if target is specified */
    createRtc(mode: 'browser' | 'headless', target?: string, signal?: AbortSignal): Promise<string> {
        this.mode = mode;
        this.listening = true;
        if (target) {
            this.target = target;
        }

        const conn = new RTCPeerConnection();
        this.rtc = conn;
        return new Promise((res, rej) => {
            if (signal?.aborted) {
                rej(new RtcComponentError('CANCEL'));
                return;
            }

            signal?.addEventListener('abort', () => {
                console.log('Signal aborted, closing RTC');
                this.closeRtc();
                this.promiseObj = undefined;
                rej(new RtcComponentError('CANCEL'));
            });

            this.promiseObj = { rej, res };
            this.timeout = setTimeout(() => {
                this.closeRtc();
                this.promiseObj = undefined;
                rej(new RtcComponentError('TIMEOUT'));
            }, 10_000);

            conn.addEventListener('icecandidate', this.onIceCandidate.bind(this), { signal: this.signal });

            const dataChannel = conn.createDataChannel('devicetodevice', {
                id: 0,
                negotiated: true,
                ordered: true,
            });
            dataChannel.binaryType = 'arraybuffer';

            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            dataChannel.addEventListener('open', this.onDataChannelOpen.bind(this), { signal: this.signal });

            dataChannel.addEventListener('message', this.onDataChannelMessage.bind(this), { signal: this.signal });

            this.dataChannel = dataChannel;

            if (target) {
                conn.createOffer()
                    .then((offer) => {
                        if (!this.rtc || this.rtc.signalingState !== 'stable') {
                            console.log('Offer created but in wrong state', this.rtc?.signalingState);
                            return;
                        }
                        this.rtc
                            .setLocalDescription(offer)
                            .then(() => {
                                this.ws?.send(
                                    JSON.stringify({
                                        from: this.identity,
                                        mode: this.mode,
                                        sdp: this.rtc!.localDescription,
                                        to: this.target,
                                        type: 'offer',
                                    }),
                                );
                            })
                            .catch(console.error);
                    })
                    .catch(console.error);
            }
        });
    }

    /** Close peerConnection and remove any listeners bound */
    closeRtc() {
        console.log('Closing RTC');
        if (!('__TAURI_INTERNALS__' in window)) {
            this.listening = false;
        }
        if (!this.rtc) {
            return;
        }
        this.rtc.close();
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.rtc.removeEventListener('icecandidate', this.onIceCandidate);
        this.rtc = undefined;
        this.addedCandidates = [];
        this.queuedCandidates = [];
        this.candidatesToSend = [];
        if (!this.dataChannel) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-misused-promises, @typescript-eslint/unbound-method
        this.dataChannel.removeEventListener('open', this.onDataChannelOpen);
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.dataChannel.removeEventListener('message', this.onDataChannelMessage);
        this.dataChannel = undefined;
    }

    /** Toggle public discovery query param for websocket */
    setPublicDiscovery(val: boolean) {
        this.#publicDiscovery = val;
        this.ws?.close();
    }
}
